import { useEffect, useState, type ReactElement } from 'react';
import { emberApi, isAuthRequired, type WeeklyLetter } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { navigate } from '../router';
import { useRequireAuth } from '../use-require-auth';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { HandoffButton } from '../components/HandoffButton';
import { LetterDoor, LetterSheet } from '../components/LetterSheet';
import { LetterPathView, PathStrip } from '../components/LetterPath';
import { letterAbout, talkAboutHref } from '../talk-about';
import './LetterPage.css';

export interface LetterPageProps {
  letterId: string;
  /** /me/letters/:id/read — the letter itself, past the page of their paths it opens on. */
  read?: boolean;
  path: string;
}

/**
 * One of Ember's weekly letters (§4.20, 2026-09-23 二次拍板), at its home in
 * the notebook: the sheet (LetterSheet) with the letterhead's two facts —
 * which letter this is and what days it reads — and, as its last row, the
 * door into the conversation (2026-09-24 拍板): "Have thoughts about this?"
 * opens the typed conversation with a first sentence in the field and the
 * letter travelling with that turn (talk-about.ts). The toolbar carries Send
 * — the Ember's §4.18 control — so the letter can be handed to someone
 * (/l/<token>). Opening this page is reading the letter (edge stamps read_at
 * once the letter is in hand).
 *
 * Since 2026-11-03 (founder: A 取代周信 点击就进入了C) the letter opens on the page
 * of their paths (LetterPathView), written with it; the letter is one tap on, at
 * /me/letters/:id/read, its head carrying the path small with the way back. A
 * letter written when they had no path opens on the letter.
 */
export function LetterPage({ letterId, read = false, path }: LetterPageProps): ReactElement {
  const status = useRequireAuth(path);
  const [letter, setLetter] = useState<WeeklyLetter | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    let alive = true;
    emberApi.meLetter(letterId)
      .then((r) => { if (alive) setLetter(r.letter); })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isAuthRequired(err)) navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true });
        else if ((err as { status?: number }).status === 404) {
          setMissing(true);
          trackFailed('me_letter_load', 'not_found', { letter_id: letterId });
        } else {
          setFailed(true);
          trackFailed('me_letter_load', 'load_failed', { letter_id: letterId });
        }
      });
    return () => { alive = false; };
  }, [letterId, status, path]);

  if (status !== 'authenticated') return <div className="ember-page-quiet">One moment…</div>;
  if (missing) return <PageFailure title="This letter isn’t here" detail="It may not be yours, or the link is old." logState="not_found" />;
  if (failed) return <PageFailure title="Couldn’t open this letter" detail="Refresh to try again." logState="load_failed" />;
  if (!letter) return <PageLoading />;

  const { about, draft } = letterAbout(letter);
  const page = letter.path?.paths.length ? letter.path : null;
  const pageHref = `/me/letters/${encodeURIComponent(letter.id)}`;

  if (page && !read) {
    return (
      <div className="ember-container ember-reading weekly-letter">
        <div className="weekly-letter-toolbar">
          <PageBack to="/me/letters" className="weekly-letter-back" fallbackLabel="Letters" />
          <div className="weekly-letter-toolbar-actions"><PageHome /></div>
        </div>
        <LetterPathView letter={letter} page={page} path={path} />
      </div>
    );
  }

  return (
    <div className="ember-container ember-reading weekly-letter">
      <div className="weekly-letter-toolbar">
        <PageBack to={page ? pageHref : '/me/letters'} className="weekly-letter-back" fallbackLabel={page ? 'Your path' : 'Letters'} />
        <div className="weekly-letter-toolbar-actions">
          <PageHome />
          <HandoffButton
            subject={{ kind: 'letter', id: letter.id }}
            title="A letter from Ember"
            handoffPath={letter.handoff_path}
            onHandoffPath={(handoffPath) => setLetter((l) => (l ? { ...l, handoff_path: handoffPath } : l))}
          />
        </div>
      </div>

      <LetterSheet
        letter={letter}
        facts="own"
        strip={page ? <PathStrip letterId={letter.id} page={page} /> : null}
        logSeen="me_letter_seen"
        logSeenAttrs={{ 'data-log-letter-id': letter.id }}
      >
        <LetterDoor
          title="Have thoughts about this?"
          sub="Talk it over with Ember"
          log="me_letter_talk"
          logAttrs={{ 'data-log-letter-id': letter.id }}
          onClick={() => navigate(talkAboutHref(about, draft, path))}
        />
      </LetterSheet>
    </div>
  );
}
